import {
  IJournalEmailAddress,
  ISubmissionContext
} from '@publishing-engine/domain/models';
import { IJournalSettingFormData } from '..';
import { validateEmailValue, validateWhitespace } from './generalInfoConfig';

/** One address of the form, edited as a name and an email in a single row. */
export type JournalEmailRow = {
  name: string;
  email: string;
};

export type JournalEmailsFormValues = {
  fromEmail: JournalEmailRow;
  replyTo: JournalEmailRow[];
  additionalEmails: JournalEmailRow[];
};

/**
 * The API stores reply-to as a list but the journal sends through its first
 * entry (`contact-publisher`), so only one row is offered for now. The add and
 * remove controls are already in place — raising this unlocks more rows.
 */
export const MAX_REPLY_TO_ROWS = 1;

export const EMPTY_JOURNAL_EMAIL_ROW: JournalEmailRow = { name: '', email: '' };

const toRow = (address?: IJournalEmailAddress): JournalEmailRow => ({
  name: address?.name ?? '',
  email: address?.email ?? ''
});

/**
 * The journal's stored config as the modal's starting values. Each list keeps at
 * least one row so there is always an input to type into, the same row the
 * stored values are read into when there are any.
 */
export const getJournalEmailsFormValues = (
  journal: ISubmissionContext
): JournalEmailsFormValues => {
  const journalEmails = journal.config?.journalEmails;

  const replyTo = (journalEmails?.replyTo ?? [])
    .map(toRow)
    .slice(0, MAX_REPLY_TO_ROWS);
  const additionalEmails = (journalEmails?.additionalEmails ?? []).map(toRow);

  return {
    fromEmail: toRow(journalEmails?.fromEmail),
    replyTo: replyTo.length ? replyTo : [{ ...EMPTY_JOURNAL_EMAIL_ROW }],
    additionalEmails: additionalEmails.length
      ? additionalEmails
      : [{ ...EMPTY_JOURNAL_EMAIL_ROW }]
  };
};

/** The journal's additional addresses, shown on the General tab. */
export const getJournalAdditionalEmails = (journal: ISubmissionContext) =>
  (journal.config?.journalEmails?.additionalEmails ?? [])
    .map(address => address.email)
    .filter(Boolean);

/** The addresses replies to journal mail go to, shown under the recipient. */
export const getJournalReplyToEmails = (journal: ISubmissionContext) =>
  (journal.config?.journalEmails?.replyTo ?? [])
    .map(address => address.email)
    .filter(Boolean);

/**
 * Whether a row holds anything the user meant to enter. Whitespace does not
 * count, so a name of spaces is a row still waiting to be filled rather than one
 * that obliges the user to supply an address as well — a stray space should
 * never add a second field's worth of work. A row the form has no values for
 * reads as empty.
 */
export const hasJournalEmailRowContent = (row?: JournalEmailRow): boolean =>
  Boolean(row && (row.name.trim().length > 0 || row.email.trim().length > 0));

/**
 * Validates one half of a row, given whether that half has to hold a value.
 *
 * The reply-to section is required outright, so it always asks. `fromEmail` and
 * `additionalEmails` are optional and only ask once the row holds something
 * meant to be entered — which leaves a blank row droppable on save. Text that is
 * nothing but spaces is never a value, so it is reported on its own field
 * without dragging the row's other half into it.
 */
const validateJournalEmailHalf = (
  value: string,
  row: JournalEmailRow | undefined,
  label: string,
  isSectionRequired: boolean
): true | string =>
  validateWhitespace(
    value,
    label,
    isSectionRequired || hasJournalEmailRowContent(row)
  );

export const validateJournalEmailName = (
  value: string,
  row: JournalEmailRow | undefined,
  label: string,
  isSectionRequired: boolean
): true | string =>
  validateJournalEmailHalf(value, row, label, isSectionRequired);

/** The address half of {@link validateJournalEmailName}, and its format check. */
export const validateJournalEmailAddress = (
  value: string,
  row: JournalEmailRow | undefined,
  label: string,
  isSectionRequired: boolean
): true | string => {
  const whitespaceValidation = validateJournalEmailHalf(
    value,
    row,
    label,
    isSectionRequired
  );
  if (whitespaceValidation !== true) {
    return whitespaceValidation;
  }

  // The half is allowed to stay empty, and an empty address has no shape to
  // check — only a value that got this far is worth testing against the format.
  if (!value.length) {
    return true;
  }

  return validateEmailValue(value);
};

/** Shown on every field taking part in a clash, since either one could move. */
export const DUPLICATE_JOURNAL_EMAIL_MESSAGE =
  'This email address is already used in another field';

/** Every address the form holds, tagged with the field path it was typed into. */
const collectJournalEmailAddresses = (values: JournalEmailsFormValues) => [
  { path: 'fromEmail.email', email: values.fromEmail.email },
  ...values.replyTo.map((row, index) => ({
    path: `replyTo.${index}.email`,
    email: row.email
  })),
  ...values.additionalEmails.map((row, index) => ({
    path: `additionalEmails.${index}.email`,
    email: row.email
  }))
];

/**
 * Whether an address is already held by a field other than `path`. The journal
 * keeps one set of addresses, so a duplicate is a duplicate wherever it was
 * typed: the from address clashing with reply-to is no more acceptable than two
 * additional rows clashing with each other. Blank values never match, so rows
 * the user has not filled in are not duplicates of one another.
 */
export const hasDuplicateJournalEmail = (
  values: JournalEmailsFormValues,
  path: string,
  email: string
): boolean => {
  const normalizedEmail = email.trim().toLowerCase();

  if (!normalizedEmail) {
    return false;
  }

  return collectJournalEmailAddresses(values).some(
    address =>
      address.path !== path &&
      address.email.trim().toLowerCase() === normalizedEmail
  );
};

const toAddress = (row: JournalEmailRow): IJournalEmailAddress => {
  const email = row.email.trim();
  const name = row.name.trim();

  return { email, ...(name ? { name } : {}) };
};

/**
 * Rebuilds the journal's whole email config, because the settings endpoint
 * replaces the object rather than merging it. Empty rows are dropped, and
 * reply-to is always sent as a list.
 */
export const buildJournalEmailsPayload = (
  values: JournalEmailsFormValues
): IJournalSettingFormData => {
  const fromEmail = toAddress(values.fromEmail);

  return {
    config: {
      journalEmails: {
        // A blank from address is left out rather than sent empty, which the
        // schema would reject; leaving it out is what clears a stored one.
        ...(fromEmail.email ? { fromEmail } : {}),
        additionalEmails: values.additionalEmails
          .map(toAddress)
          .filter(address => address.email.length > 0),
        replyTo: values.replyTo
          .filter(row => row.email.trim().length > 0)
          .map(toAddress)
      }
    }
  };
};
